package com.isaias.fyattravel.web.components

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.events.Event

private fun prefersReducedMotion(): Boolean =
    window.matchMedia("(prefers-reduced-motion: reduce)").matches

/**
 * A real `<img>` with native lazy-loading, a pulse-skeleton shown until it
 * finishes loading, and a graceful fallback (keeps the skeleton/wash tone
 * instead of a broken-image icon) if the file 404s — useful while real
 * photos are still being dropped into the resources folder.
 *
 * Reuses the shared `pulse` keyframe already defined once, globally, in
 * `resources/index.html` (also used by the categories-page skeleton).
 */
@Composable
fun LazyImage(
    src: String,
    alt: String,
    modifier: StyleScope.() -> Unit = {}
) {
    var loaded by remember(src) { mutableStateOf(false) }
    var errored by remember(src) { mutableStateOf(false) }
    val reducedMotion = remember { prefersReducedMotion() }

    Div({
        style {
            position(Position.Relative)
            overflow("hidden")
            backgroundColor(Color("#e7ddc9"))
            property("background-image", "linear-gradient(115deg, #efe6d5 0%, #e4d8c2 55%, #ede3d0 100%)")
            modifier()
        }
    }) {
        if (!loaded) {
            Div({
                style {
                    position(Position.Absolute)
                    property("inset", "0")
                    if (!reducedMotion) {
                        property("animation", "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite")
                    }
                    backgroundColor(Color("#ddd0b3"))
                }
            })
        }
        if (!errored) {
            Img(src = src, alt = alt, attrs = {
                attr("loading", "lazy")
                style {
                    width(100.percent)
                    height(100.percent)
                    property("object-fit", "cover")
                    property("object-position", "center")
                    opacity(if (loaded) 1.0 else 0.0)
                    if (!reducedMotion) {
                        property("transition", "opacity 0.4s ease")
                    }
                }
                ref { element ->
                    val img = element as HTMLImageElement
                    if (img.complete && img.naturalWidth > 0) {
                        loaded = true
                    }
                    val onLoad: (Event) -> Unit = { loaded = true }
                    val onError: (Event) -> Unit = { errored = true }
                    img.addEventListener("load", onLoad)
                    img.addEventListener("error", onError)
                    onDispose {
                        img.removeEventListener("load", onLoad)
                        img.removeEventListener("error", onError)
                    }
                }
            })
        }
    }
}
